import {
    Typography, Paper, Box, TextField,
    ToggleButton, ToggleButtonGroup, Divider
} from '@mui/material';
import { useState } from 'react';
import { observer } from 'mobx-react-lite';
import searchStore from '../../Mobx/SearchStore';

// עיצוב קומפקטי לכפתורים
const compactButtonStyle = {
    px: 1,
    py: 0.3,
    minHeight: '28px',
    fontSize: '0.75rem',
    borderRadius: '6px',
    whiteSpace: 'nowrap',
    textTransform: 'none',
    '&.Mui-selected': {
        backgroundColor: '#1976d2',
        color: 'white',
        // fontWeight: 'bold',
    },
    '&.Mui-selected:hover': {
        backgroundColor: '#1565c0',
    },
};

// עיצוב קומפקטי לשדות טקסט
const compactTextFieldStyle = {
    width: '60px',
    fontSize: '0.75rem',
    padding: 4,
};

const SearchOptions = observer(() => {
    const [fuzzyValues, setFuzzyValues] = useState({
        maxEdits: searchStore.fuzzy.maxEdits,
        prefixLength: searchStore.fuzzy.prefixLength,
        maxExpansions: searchStore.fuzzy.maxExpansions,
    });

    const handleFuzzyChange = (key, val) => {
        const num = +val;
        setFuzzyValues(prev => ({ ...prev, [key]: val }));
        searchStore.setFuzzy(key, num);
    };

    return (
        <Paper sx={{ p: 1, display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>הגדרות</Typography>
            <Box sx={{ overflow: 'auto', flex: 1, minHeight: 0 }}>

                {/* סוג מילון */}
                <Box mb={1}>
                    <Typography variant="caption" sx={{ display: 'block', mb: 0.3 }}>מילון</Typography>
                    <Box display="flex" flexWrap="wrap" gap={1}>
                        <ToggleButtonGroup
                            size="small"
                            value={searchStore.dictionaryType}
                            exclusive
                            onChange={(e, val) => val && searchStore.setDictionaryType(val)}
                        >
                            <ToggleButton value="dictionary_eng_to_heb" sx={compactButtonStyle}>אנגלי - עברי</ToggleButton>
                            <ToggleButton value="dictionary_heb_to_eng" sx={compactButtonStyle}>עברי - אנגלי</ToggleButton>
                        </ToggleButtonGroup>
                    </Box>
                </Box>

                {/* מחלק: חיפוש */}
                <Divider sx={{ my: 1 }}>
                    <Typography variant="caption" fontWeight="bold">חיפוש</Typography>
                </Divider>

                {/* אופרטור */}
                <Box mb={1}>
                    <Typography variant="caption" sx={{ display: 'block', mb: 0.3 }}>סוג חיפוש</Typography>
                    <Box display="flex" flexWrap="wrap" gap={1}>
                        <ToggleButtonGroup
                            size="small"
                            value={searchStore.operator}
                            exclusive
                            onChange={(e, val) => val && searchStore.setOperator(val)}
                        >
                            <ToggleButton value="autocomplete" sx={compactButtonStyle}>השלמה אוטומטית</ToggleButton>
                            <ToggleButton value="text" sx={compactButtonStyle}>טקסט</ToggleButton>
                            <ToggleButton value="knnBeta" sx={compactButtonStyle}>סמנטי</ToggleButton>
                            {/* <ToggleButton value="compound" sx={compactButtonStyle}>מורכב</ToggleButton> */}
                        </ToggleButtonGroup>
                    </Box>
                </Box>

                {(searchStore.operator === 'text' || searchStore.operator === 'autocomplete') && (
                    <>
                        {/* maxEdits */}
                        <Box mb={1}>
                            <Typography variant="caption" sx={{ display: 'block', mb: 0.3 }}>כמה טעויות מותר</Typography>
                            <Box display="flex" flexWrap="wrap" gap={1}>
                                <ToggleButtonGroup
                                    size="small"
                                    value={Number(fuzzyValues.maxEdits)}
                                    exclusive
                                    onChange={(e, val) => val && handleFuzzyChange('maxEdits', val)}
                                >
                                    <ToggleButton value={1} sx={compactButtonStyle}>1</ToggleButton>
                                    <ToggleButton value={2} sx={compactButtonStyle}>2</ToggleButton>
                                </ToggleButtonGroup>
                            </Box>
                        </Box>

                        {/* prefixLength */}
                        <Box mb={1}>
                            <Typography variant="caption" sx={{ display: 'block', mb: 0.3 }}>כמה תווים ראשונים חייבים להיות מדויקים</Typography>
                            <TextField
                                type="number"
                                size="small"
                                value={fuzzyValues.prefixLength}
                                onChange={(e) => {
                                    const val = Math.max(1, +e.target.value || 1);
                                    setFuzzyValues(prev => ({ ...prev, prefixLength: val }));
                                    searchStore.setFuzzy('prefixLength', val);
                                }}
                                sx={{ width: compactTextFieldStyle.width }}
                                inputProps={{ style: compactTextFieldStyle, min: 1 }}
                            />
                        </Box>

                        {/* maxExpansions */}
                        <Box mb={1}>
                            <Typography variant="caption" sx={{ display: 'block', mb: 0.3 }}>כמה התאמות שונות תיבדקנה (בין 1 ל־500. משפיע על ביצועים)</Typography>
                            <TextField
                                type="number"
                                size="small"
                                value={fuzzyValues.maxExpansions}
                                onChange={(e) => {
                                    const val = Math.max(1, Math.min(500, +e.target.value || 1));
                                    setFuzzyValues(prev => ({ ...prev, maxExpansions: val }));
                                    searchStore.setFuzzy('maxExpansions', val);
                                }}
                                sx={{ width: compactTextFieldStyle.width }}
                                inputProps={{ style: compactTextFieldStyle, min: 1, max: 500 }}
                            />
                        </Box>
                    </>
                )}

                {/* מחלק: פילטרים */}
                <Divider sx={{ my: 1 }}>
                    <Typography variant="caption" fontWeight="bold">פילטרים</Typography>
                </Divider>

                {/* בנדים */}
                <Box mb={1}>
                    <Typography variant="caption" sx={{ display: 'block', mb: 0.3 }}>רמות (Band)</Typography>
                    <Box display="flex" flexWrap="wrap" gap={1}>
                        {[1, 2, 3, 4, 5, 6].map((band) => (
                            <ToggleButton
                                key={band}
                                value={band}
                                sx={compactButtonStyle}
                                selected={searchStore.filters.band.values.includes(band)}
                                onClick={() => {
                                    const current = searchStore.filters.band.values;
                                    const newValues = current.includes(band)
                                        ? current.filter(v => v !== band)
                                        : [...current, band];
                                    searchStore.setBandFilter(newValues);
                                }}
                            >
                                {band}
                            </ToggleButton>
                        ))}
                    </Box>
                </Box>

                {/* כמות תוצאות */}
                <Box mb={1}>
                    <Typography variant="caption" sx={{ display: 'block', mb: 0.3 }}>כמות תוצאות</Typography>
                    <TextField
                        type="number"
                        size="small"
                        value={searchStore.limit}
                        onChange={(e) => {
                            const val = Math.max(1, Math.min(1000, +e.target.value || 1));
                            searchStore.setLimit(val);
                        }}
                        sx={{ width: compactTextFieldStyle.width }}
                        inputProps={{ style: compactTextFieldStyle, min: 1, max: 1000 }}
                    />
                </Box>

            </Box>
        </Paper>
    );
});

export default SearchOptions;
